import { Button, Card, DataTable, EmptyState, FormField, MoneyText, Select, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  usePlatformInvoices,
  usePlatformPermissions,
  useRefreshPlatform,
  useTenantOptions,
} from "../../../features/platform/queries.js";
import { PlatformPage } from "../../../features/platform/shared.js";
import { useInvoiceColumns } from "../../../features/platform/tenant-tabs.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/platform/faturalar/")({ component: InvoicesRoute });

const STATUSES = ["OPEN", "OVERDUE", "PAID", "VOID"] as const;

/**
 * Platform faturaları (Prompt 24 web): dönem sonu kesilen abonelik + kullanım faturaları, açık / gecikmiş toplamları,
 * e-belge durumu. "Faturalamayı çalıştır" biten dönemleri keser ve gecikme kurallarını uygular (zamanlanmış iş de aynısını yapar).
 */
function InvoicesRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshPlatform();
  const permissions = usePlatformPermissions();
  const tenants = useTenantOptions();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const pages = useCursorPages(`${tenantId}|${status}`);
  const invoices = usePlatformInvoices({ tenantId, status }, pages.cursor);
  const columns = useInvoiceColumns(true);
  const run = useMutation({
    mutationFn: async () => (await api.POST("/platform/billing/run", { body: { tenantId: null } })).data!,
    onSuccess: async (result) => {
      await refresh();
      toast({
        tone: result.invoices.length > 0 ? "success" : "info",
        title:
          result.invoices.length > 0
            ? t("platform.invoices.runIssued", { count: result.invoices.length })
            : t("platform.invoices.runNothing"),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <PlatformPage
      title={t("platform.invoices.title")}
      description={t("platform.invoices.description")}
      permission="platform.billing.read"
      actions={
        permissions.has("platform.billing.manage") ? (
          <Button loading={run.isPending} onClick={() => run.mutate()}>
            {t("platform.invoices.runAll")}
          </Button>
        ) : null
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">{t("platform.overview.outstanding")}</p>
          <p className="text-2xl font-semibold">
            <MoneyText value={invoices.data?.totals.outstanding ?? "0"} />
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">{t("platform.overview.overdue")}</p>
          <p className="text-2xl font-semibold">
            <MoneyText value={invoices.data?.totals.overdue ?? "0"} />
          </p>
        </Card>
      </div>
      <DataTable
        caption={t("platform.invoices.title")}
        columns={columns}
        rows={invoices.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={invoices.isPending}
        error={invoices.error ? errorMessage(invoices.error) : undefined}
        onRowClick={(row) => void navigate({ to: "/platform/faturalar/$id", params: { id: row.id } })}
        emptyState={<EmptyState icon={<FileText />} title={t("platform.invoices.empty")} />}
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: invoices.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(invoices.data?.page.nextCursor),
        }}
        filters={
          <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label={t("platform.fields.tenant")}>
              {(control) => (
                <Select
                  {...control}
                  value={tenantId ?? ""}
                  onChange={(event) => setTenantId(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {(tenants.data ?? []).map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("platform.fields.status")}>
              {(control) => (
                <Select
                  {...control}
                  value={status ?? ""}
                  onChange={(event) => setStatus(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(`platform.invoices.state.${value}`)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </div>
        }
      />
    </PlatformPage>
  );
}
